import React from 'react';
import { css } from '@emotion/react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '../../../components/Button';
import { FormControl } from '../../../components/FormControl';
import { FormFooter } from '../../../components/FormFooter';
import { FormLine } from '../../../components/FormLine';
import { FormSectionHeading } from '../../../components/FormSectionHeading';
import { Heading } from '../../../components/Heading';
import { Input } from '../../../components/Input';
import { Radios } from '../../../components/Radios';
import { Toggle } from '../../../components/Toggle';
import { useStep } from '../hooks/useStep';
import { PROCESS_STEP } from 'routes';
import {
	QUERY_REQUEST_DATA,
	QUERY_REQUEST_STATE,
	MUTATION_UPDATE_DATA_ACCOMODATION,
} from 'queries/queries-booking';
import { useMutation, useQuery } from 'urql';
import { SpinnerBlock } from 'components/SpinnerBlock';
import {
	timeValidation,
	urlGoogleMapsValidation,
	urlValidation,
	zipValidation,
	phoneValidation,
	nonEmptyValidation,
} from '../forms/validations';
import { transformTime } from 'utils/utils';
import { useStore } from 'hooks/useStore';
import { format } from 'date-fns';
import { theme } from 'styles/theme';
import { OptionalInfoBox } from '../components/OptionalInfoBox';
import { PhoneInput } from 'components/PhoneInput';
import { useRequestStateQuery } from 'generated/graphql';

enum FORM_ACCOMODATION {
	BASE = 'accomodation',
	TYPE = 'accomodation_type',
	NAME = 'accomodation_name',
	ADDRESS_STREET = 'accomodation_street',
	ADDRESS_CITY = 'accomodation_city',
	ADDRESS_ZIP = 'accomodation_zip',
	ADDRESS_GPS = 'accomodation_gps',
	EVENT_DISTANCE = 'accomodation_event_distance',
	WEB = 'accomodation_web',
	PHONE = 'accomodation_phone',
	CHECK_IN = 'accomodation_check_in',
	CHECK_OUT = 'accomodation_check_out',
	BREAKTFAST = 'accomodation_breakfast',
	INTERNET_ACCESS = 'accomodation_internet_access',
}

export const AccomodationStep = () => {
	const { id } = useStep(PROCESS_STEP.ACOOMODATION);

	const [queryRequestData] = useQuery({
		query: QUERY_REQUEST_DATA,
		variables: {
			url: id,
		},
	});

	const [_stateRequest] = useRequestStateQuery({
		variables: { url: id },
	});

	const accomodationUpdateAt = queryRequestData?.data?.request?.accomodation_update_at;

	return (
		<div
			css={css`
				width: 560px;
			`}
		>
			<Heading
				title="Ubytování"
				desc={
					<>
						<OptionalInfoBox />
						{!!accomodationUpdateAt && queryRequestData?.data?.request?.confirmed && (
							<div
								css={css`
									border: 1px solid #f1f1f4;
									border-radius: 12px;
									background: #fafaff;
									padding: 12px 20px;
									margin-top: 8px;
									text-align: center;
									font-size: 14px;
									line-height: 20px;
									color: ${theme.color.text['400']};
								`}
							>
								<strong
									css={css`
										font-weight: 500;
									`}
								>
									Poslední aktualizace:
								</strong>{' '}
								{format(new Date(accomodationUpdateAt), 'dd. MM. YYY, HH:mm')}
							</div>
						)}
					</>
				}
			/>

			{queryRequestData.fetching ? (
				<SpinnerBlock />
			) : (
				<Form defaultValues={queryRequestData?.data?.request} url={id} />
			)}
		</div>
	);
};

const Form = ({ defaultValues, url }) => {
	const { notifications } = useStore();

	const { goNextStep, goPrevStep } = useStep();

	const localDefaultValues = {
		accomodation: defaultValues?.accomodation,
		accomodation_type: defaultValues?.accomodation_type,
		accomodation_name: defaultValues?.accomodation_name,
		accomodation_street: defaultValues?.accomodation_street,
		accomodation_city: defaultValues?.accomodation_city,
		accomodation_zip: defaultValues?.accomodation_zip,
		accomodation_gps: defaultValues?.accomodation_gps,

		accomodation_event_distance: defaultValues?.accomodation_event_distance,
		accomodation_web: defaultValues?.accomodation_web,
		accomodation_phone: defaultValues?.accomodation_phone,

		accomodation_check_in: transformTime(defaultValues?.accomodation_check_in),
		accomodation_check_out: transformTime(defaultValues?.accomodation_check_out),

		accomodation_breakfast: defaultValues?.accomodation_breakfast,
		accomodation_internet_access: defaultValues?.accomodation_internet_access,
	};

	const {
		handleSubmit,
		register,
		control,
		watch,
		formState: { errors },
	} = useForm({ defaultValues: localDefaultValues });

	// Submit Form
	const [updateDataAccomodation, executeUpdateDataAccomodation] = useMutation(
		MUTATION_UPDATE_DATA_ACCOMODATION,
	);

	const onSubmit = (data: any) => {
		// console.log('SUBMIT');
		// console.log(data);

		// todo: tohle je trosku naprasene, ale funguje to, finalne ma byt 12 znaku prozatim, je to i v tech stepu
		if (data.accomodation_phone !== null && data.accomodation_phone.length <= 6) {
			data.accomodation_phone = null;
		}

		executeUpdateDataAccomodation({
			url: url,
			input: data,
		}).then((result) => {
			// console.log(result);

			if (result?.error) {
				// VYHODIT ERROR
				return false;
			} else {
				notifications.addNotification({ message: 'Uloženo' });
			}

			goNextStep();
		});
	};

	const labelWidth = 213;

	const accomodationTypeOptions = [
		{
			label: 'Hotel',
			value: 'ACCOMODATION_HOTEL',
		},
		{ label: 'Pension', value: 'ACCOMODATION_PENSION' },
		{ label: 'Privát', value: 'ACCOMODATION_PRIVATE' },
	];

	const booleanOptions = [
		{
			label: 'Ano',
			value: true,
		},
		{ label: 'Ne', value: false },
	];

	const accomodationEnabled = watch(FORM_ACCOMODATION.BASE, defaultValues.accomodation);
	const accomodationType = watch(FORM_ACCOMODATION.TYPE, 'ACCOMODATION_HOTEL');

	const accomodationDescOptions = {
		ACCOMODATION_HOTEL: { whose: 'hotelu' },
		ACCOMODATION_PENSION: { whose: 'pensionu' },
		ACCOMODATION_PRIVATE: { whose: 'privátu' },
	};
	const accomodationDesc = accomodationDescOptions[accomodationType];

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FormSectionHeading>Poskytujete ubytování?</FormSectionHeading>

			<FormLine first>
				<Controller
					name={FORM_ACCOMODATION.BASE}
					control={control}
					defaultValue={true}
					render={({ field }) => {
						return <Toggle {...field}>ANO, poskytujeme ubytování</Toggle>;
					}}
				/>
			</FormLine>

			{accomodationEnabled && (
				<>
					<FormLine>
						<FormControl
							label="Typ"
							htmlFor={FORM_ACCOMODATION.TYPE}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.TYPE]}
							spaceMedium
						>
							<Controller
								name={FORM_ACCOMODATION.TYPE}
								control={control}
								defaultValue={accomodationTypeOptions[0].value}
								render={({ field }) => {
									return <Radios options={accomodationTypeOptions} {...field} />;
								}}
							/>
						</FormControl>
					</FormLine>

					<FormSectionHeading>Adresa</FormSectionHeading>

					<FormLine first>
						<FormControl
							label="Název ubytování"
							htmlFor={FORM_ACCOMODATION.NAME}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.NAME]}
						>
							<Input
								placeholder={'Název ubytování...'}
								{...register(FORM_ACCOMODATION.NAME, nonEmptyValidation())}
								error={!!errors[FORM_ACCOMODATION.NAME]}
							/>
						</FormControl>
					</FormLine>

					<FormLine>
						<FormControl
							label="Ulice"
							htmlFor={FORM_ACCOMODATION.ADDRESS_STREET}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.ADDRESS_STREET]}
						>
							<Input
								placeholder={'Ulice ' + accomodationDesc.whose + '...'}
								{...register(FORM_ACCOMODATION.ADDRESS_STREET, nonEmptyValidation())}
								error={!!errors[FORM_ACCOMODATION.ADDRESS_STREET]}
							/>
						</FormControl>
					</FormLine>

					<FormLine>
						<FormControl
							label="Město"
							htmlFor={FORM_ACCOMODATION.ADDRESS_CITY}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.ADDRESS_CITY]}
						>
							<Input
								placeholder={'Město ' + accomodationDesc.whose + '...'}
								{...register(FORM_ACCOMODATION.ADDRESS_CITY, nonEmptyValidation())}
								error={!!errors[FORM_ACCOMODATION.ADDRESS_CITY]}
							/>
						</FormControl>
					</FormLine>

					<FormLine>
						<FormControl
							label="PSČ"
							htmlFor={FORM_ACCOMODATION.ADDRESS_ZIP}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.ADDRESS_ZIP]}
						>
							<Input
								{...register(FORM_ACCOMODATION.ADDRESS_ZIP, zipValidation())}
								error={!!errors[FORM_ACCOMODATION.ADDRESS_ZIP]}
								width={80}
							/>
						</FormControl>
					</FormLine>

					<FormLine>
						<FormControl
							label="GPS Souřadnice"
							htmlFor={FORM_ACCOMODATION.ADDRESS_GPS}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.ADDRESS_GPS]}
							desc="URL Odkaz na Google Maps"
						>
							<Input
								placeholder="https://"
								{...register(
									FORM_ACCOMODATION.ADDRESS_GPS,
									urlGoogleMapsValidation({
										url: 'Neplatný URL odkaz na Google Maps.',
									}),
								)}
								error={!!errors[FORM_ACCOMODATION.ADDRESS_GPS]}
							/>
						</FormControl>
					</FormLine>

					<FormLine>
						<FormControl
							label="Vzdálenost od klubu (km)"
							htmlFor={FORM_ACCOMODATION.EVENT_DISTANCE}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.EVENT_DISTANCE]}
							desc="Je-li vzdálenost 0 km, zadejte hodnotu 0."
						>
							<Input
								placeholder="0 km..."
								{...register(FORM_ACCOMODATION.EVENT_DISTANCE, { valueAsNumber: true })}
								error={!!errors[FORM_ACCOMODATION.EVENT_DISTANCE]}
								width={120}
								isNumber
							/>
						</FormControl>
					</FormLine>

					<FormSectionHeading>Kontakt</FormSectionHeading>

					<FormLine first>
						<FormControl
							label="Web"
							htmlFor={FORM_ACCOMODATION.WEB}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.WEB]}
						>
							<Input
								placeholder="https://"
								{...register(
									FORM_ACCOMODATION.WEB,
									urlValidation({
										url: 'Neplatný odkaz na web.',
									}),
								)}
								error={!!errors[FORM_ACCOMODATION.WEB]}
							/>
						</FormControl>
					</FormLine>

					<FormLine>
						<FormControl
							label="Telefonní číslo"
							htmlFor={FORM_ACCOMODATION.PHONE}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.PHONE]}
						>
							<Controller
								name={FORM_ACCOMODATION.PHONE}
								control={control}
								rules={{
									...phoneValidation(),
								}}
								render={({ field }) => {
									return (
										<PhoneInput {...field} width={240} error={!!errors[FORM_ACCOMODATION.PHONE]} />
									);
								}}
							/>
						</FormControl>
					</FormLine>

					<FormSectionHeading>Časy</FormSectionHeading>

					<FormLine first>
						<FormControl
							label="Check-in"
							htmlFor={FORM_ACCOMODATION.CHECK_IN}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.CHECK_IN]}
						>
							<Input
								width={64}
								placeholder="00:00"
								{...register(FORM_ACCOMODATION.CHECK_IN, timeValidation())}
								error={!!errors[FORM_ACCOMODATION.CHECK_IN]}
							/>
						</FormControl>
					</FormLine>

					<FormLine>
						<FormControl
							label="Check-out"
							desc="Preferován pozdní check-out"
							htmlFor={FORM_ACCOMODATION.CHECK_OUT}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.CHECK_OUT]}
						>
							<Input
								width={64}
								placeholder="00:00"
								{...register(FORM_ACCOMODATION.CHECK_OUT, timeValidation())}
								error={!!errors[FORM_ACCOMODATION.CHECK_OUT]}
							/>
						</FormControl>
					</FormLine>

					<FormSectionHeading>Služby</FormSectionHeading>

					<FormLine first>
						<FormControl
							label="Snídaně"
							htmlFor={FORM_ACCOMODATION.BREAKTFAST}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.BREAKTFAST]}
							spaceMedium
						>
							<Controller
								name={FORM_ACCOMODATION.BREAKTFAST}
								control={control}
								defaultValue={booleanOptions[0].value}
								render={({ field }) => {
									return <Radios options={booleanOptions} {...field} />;
								}}
							/>
						</FormControl>
					</FormLine>

					<FormLine>
						<FormControl
							label="Internet / Wi-fi"
							htmlFor={FORM_ACCOMODATION.INTERNET_ACCESS}
							labelWidth={labelWidth}
							error={errors[FORM_ACCOMODATION.INTERNET_ACCESS]}
							spaceMedium
						>
							<Controller
								name={FORM_ACCOMODATION.INTERNET_ACCESS}
								control={control}
								defaultValue={booleanOptions[0].value}
								render={({ field }) => {
									return <Radios options={booleanOptions} {...field} />;
								}}
							/>
						</FormControl>
					</FormLine>
				</>
			)}

			<FormFooter>
				<Button outline type="button" onClick={() => goPrevStep()}>
					Zpět
				</Button>
				<Button type="submit" isProcessing={updateDataAccomodation.fetching}>
					{defaultValues?.confirmed ? 'Aktualizovat' : 'Uložit a pokračovat'}
				</Button>
			</FormFooter>
		</form>
	);
};
